import cookies from 'js-cookie';

export interface StorageOptions {
  domain?: string;
  sameSite?: 'None' | 'Lax' | 'Strict';
  secure?: boolean;
  expires?: number;
}

export interface StorageItem {
  value: string;
  timestamp: number;
  expiresAt?: number;
}

class StorageManager {
  private cookieTestKey = '__cookie_test__';

  /**
   * Test if cookies are available and working
   */
  private testCookieAvailability(): boolean {
    try {
      const testValue = 'test';
      cookies.set(this.cookieTestKey, testValue, {
        domain: window.location.hostname,
        sameSite: 'None',
        secure: true,
      });
      const retrieved = cookies.get(this.cookieTestKey);
      cookies.remove(this.cookieTestKey, {
        domain: window.location.hostname,
        sameSite: 'None',
        secure: true,
      });
      return retrieved === testValue;
    } catch {
      return false;
    }
  }

  /**
   * Get the appropriate storage method (cookies or localStorage)
   */
  private getStorageMethod(): 'cookies' | 'localStorage' {
    // Always test cookie availability to determine which storage to use
    return this.testCookieAvailability() ? 'cookies' : 'localStorage';
  }

  /**
   * Set a value in storage
   */
  set(key: string, value: string, options: StorageOptions = {}): void {
    const method = this.getStorageMethod();

    if (method === 'cookies') {
      const cookieOptions = {
        domain: options.domain || window.location.hostname,
        sameSite: options.sameSite || 'None',
        secure: options.secure !== false,
        ...(options.expires && { expires: options.expires }),
      };
      cookies.set(key, value, cookieOptions);
      return;
    }

    // Use localStorage with metadata
    const item: StorageItem = {
      value,
      timestamp: Date.now(),
      ...(options.expires && { expiresAt: Date.now() + options.expires * 24 * 60 * 60 * 1000 }),
    };
    localStorage.setItem(key, JSON.stringify(item));
  }

  /**
   * Get a value from storage
   */
  get(key: string): string | null {
    const method = this.getStorageMethod();

    if (method === 'cookies') {
      return cookies.get(key) || null;
    }

    const stored = localStorage.getItem(key);
    if (!stored) {
      return null;
    }

    try {
      const item: StorageItem = JSON.parse(stored);

      // Check if item has expired
      if (item.expiresAt && item.expiresAt < Date.now()) {
        this.remove(key);
        return null;
      }

      return item.value;
    } catch {
      // If parsing fails, remove the corrupted item
      this.remove(key);
      return null;
    }
  }

  /**
   * Remove a value from storage
   */
  remove(key: string, options: StorageOptions = {}): void {
    const method = this.getStorageMethod();

    if (method === 'cookies') {
      const cookieOptions = {
        domain: options.domain || window.location.hostname,
        sameSite: options.sameSite || 'None',
        secure: options.secure !== false,
      };
      cookies.remove(key, cookieOptions);
      return;
    }

    localStorage.removeItem(key);
  }

  /**
   * Check if a key exists in storage
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Clear all storage for this app
   */
  clear(): void {
    const method = this.getStorageMethod();

    if (method === 'cookies') {
      // Note: This will only clear cookies that we know about
      // In a real implementation, you might want to maintain a list of known keys
      console.warn('StorageManager.clear() called with cookies - this may not clear all cookies');
      return;
    }

    // Clear all localStorage items
    localStorage.clear();
  }
}

// Export a singleton instance
export const storageManager = new StorageManager();

// Export convenience functions
export const setStorage = (key: string, value: string, options?: StorageOptions) => {
  storageManager.set(key, value, options);
};

export const getStorage = (key: string) => {
  return storageManager.get(key);
};

export const removeStorage = (key: string, options?: StorageOptions) => {
  storageManager.remove(key, options);
};

export const hasStorage = (key: string) => {
  return storageManager.has(key);
};

export const clearStorage = () => {
  storageManager.clear();
};
